import {
  AvailableCarriersRequest,
  CarrierPreviewItem,
  CarrierPreviewRequest,
  Order,
  ServiceTypeEnum,
} from '@/types';

/**
 * Builds CarrierPreviewRequest from Order data
 */
export const buildCarrierPreviewRequest = (
  orderData: Order,
  sourceRegionUid: string,
): CarrierPreviewRequest => {
  // Calculate total products value
  const productsValue = (orderData.products || []).reduce((sum, product) => {
    const price = parseFloat(String(product.price)) || 0;
    const quantity = Number(product.quantity) || 1;
    return sum + price * quantity;
  }, 0);

  // Convert OrderOption[] to string[] for carrier preview API
  // Some flows (e.g., EditOrder live form state) may not populate `orderData.options`
  // but still provide `id_check` / `clue_verification` booleans.
  const optionsArrayFromOptions: string[] = (orderData.options || []).map((opt) =>
    typeof opt === 'string' ? opt : opt.type,
  );
  const optionsArray: string[] =
    optionsArrayFromOptions.length > 0
      ? optionsArrayFromOptions
      : [
          ...(orderData.id_check ? ['id_check'] : []),
          ...(orderData.clue_verification ? ['clue_link_verification'] : []),
        ];

  return {
    source_region_uid: sourceRegionUid,
    destination_region_uid: orderData.contact?.region_uid || '',
    request_time: new Date().toISOString(), // Already returns UTC time
    size: orderData.size || 'S',
    is_fragile: orderData.is_fragile || false,
    type: orderData.type || 'DEFAULT',
    products_value: String(productsValue),
    options: optionsArray,
  };
};

/**
 * Builds AvailableCarriersRequest for the new POST /orders/available-carriers endpoint.
 *
 * Bug E (2026-05-17): when a shipping rule restricts the carrier set, the
 * caller forwards the restricted_carrier_uids here so the backend can ensure
 * every rule-mandated carrier surfaces in the response — even when
 * DispatchService would drop them for missing pricing rows. The backend
 * returns missing-pricing carriers with shipping_fee=null +
 * needs_pricing_quote=true so the UI renders a "Price on request" affordance
 * instead of silently honouring the pricing gap and dropping the carrier.
 */
export const buildAvailableCarriersRequest = (
  orderData: Order,
  sourceRegionUid: string,
  restrictedCarrierUids?: string[],
): AvailableCarriersRequest => {
  const productsValue = (orderData.products || []).reduce((sum, product) => {
    const price = parseFloat(String(product.price)) || 0;
    const quantity = Number(product.quantity) || 1;
    return sum + price * quantity;
  }, 0);

  const optionsArrayFromOptions: string[] = (orderData.options || []).map((opt) =>
    typeof opt === 'string' ? opt : opt.type,
  );
  const optionsArray: string[] =
    optionsArrayFromOptions.length > 0
      ? optionsArrayFromOptions
      : [
          ...(orderData.id_check ? ['id_check'] : []),
          ...(orderData.clue_verification ? ['clue_link_verification'] : []),
        ];

  return {
    source_region_uid: sourceRegionUid,
    destination_region_uid: orderData.contact?.region_uid || '',
    type: orderData.type || 'DEFAULT',
    size: orderData.size || 'S',
    is_fragile: orderData.is_fragile || false,
    // TEMP-5: backend filters the carrier list down to temperature-capable
    // carriers for the destination province when true.
    temperature_control: orderData.temperature_control || false,
    products_value: productsValue,
    options: optionsArray,
    ...(restrictedCarrierUids && restrictedCarrierUids.length > 0
      ? { restricted_carrier_uids: restrictedCarrierUids }
      : {}),
  };
};

/**
 * Processes carrier preview response and tags carriers with service type
 */
export const processCarrierPreviewResponse = (response: {
  object: {
    same_day_carriers?: CarrierPreviewItem[];
    next_day_carriers?: CarrierPreviewItem[];
  };
}): CarrierPreviewItem[] => {
  // Tag same-day carriers with serviceType
  const sameDayCarriers = (response.object.same_day_carriers || []).map((carrier) => ({
    ...carrier,
    serviceType: ServiceTypeEnum.SAME_DAY,
  }));

  // Tag next-day carriers with serviceType
  const nextDayCarriers = (response.object.next_day_carriers || []).map((carrier) => ({
    ...carrier,
    serviceType: ServiceTypeEnum.NEXT_DAY,
  }));

  // Combine both arrays
  return [...sameDayCarriers, ...nextDayCarriers];
};
